import { type FormatState, NOT_EDITABLE_STATUSES_FORMAT } from '@/constants/formatState'
import type { Format } from '@/types/formats.ts'
import { addDays, eachDayOfInterval, format, isValid, parseISO, startOfDay } from 'date-fns'
import type { Event } from '@/types/event.ts'
import { useTranslation } from 'i18next-vue'
import type { StrapiPopulate } from '@/types/strapi.ts'
import { parseDateOnlyFromDateTimeIso } from '@/utils/date-helper.ts'

export const FORMAT_POPULATE = {
    image: true,
    events: true,
    documents: true,
    institutions: { populate: ['logo'] },
    topicTags: true,
    company: {
        populate: ['logo', 'location'],
    },
    application_form: true,
} as StrapiPopulate

// Date-only deadlines include the entire selected day in the browser's local timezone.
export const getApplicationClosingDate = (endDate: string): Date =>
    addDays(startOfDay(parseISO(endDate)), 1)

export const getDefaultAccommodationNights = (event: Event): string[] => {
    if (!event.startDate || !event.endDate) return []

    const parsedStartDate = event.timeNotDefined
        ? parseDateOnlyFromDateTimeIso(event.startDate)
        : parseISO(event.startDate)
    const parsedEndDate = event.timeNotDefined
        ? parseDateOnlyFromDateTimeIso(event.endDate)
        : parseISO(event.endDate)

    if (!parsedStartDate || !parsedEndDate) return []

    if (!isValid(parsedStartDate) || !isValid(parsedEndDate)) return []

    const startDate = new Date(
        parsedStartDate.getFullYear(),
        parsedStartDate.getMonth(),
        parsedStartDate.getDate()
    )
    const endDate = new Date(
        parsedEndDate.getFullYear(),
        parsedEndDate.getMonth(),
        parsedEndDate.getDate()
    )

    if (startDate >= endDate) return []

    return [
        'arrival',
        ...eachDayOfInterval({ start: startDate, end: endDate }).map(
            (day: Date): string => `during-${format(day, 'yyyy-MM-dd')}`
        ),
    ]
}

// Why: The application window applies to the whole range between the start and the end date,
// including the complete calendar day. The application starts at "00:00:00" and the end date
// contains the whole day and ends at "23:59:59".
export const isFormatApplicationOpened = (format: Format | undefined): boolean => {
    if (!format) return false

    const openingDate = format.applicationStartDate
    const closingDate = format.applicationEndDate
    const today = new Date().getTime()

    if (!openingDate) {
        return false
    }

    const opensAt = parseISO(openingDate).getTime()

    if (today < opensAt) {
        return false
    }

    if (!closingDate) {
        return true
    }

    const closesAt = getApplicationClosingDate(closingDate).getTime()

    return today < closesAt
}

// Why: a format counts as completed once every event has ended. Deleting such a format only
// destroys historical data, so the UI must warn differently than for an active format.
export const isFormatCompleted = (events: Event[] | undefined): boolean => {
    if (!events || events.length === 0) {
        return false
    }

    const today = new Date().getTime()

    return events.every((event) => {
        if (!event.endDate) {
            return false
        }
        return parseISO(event.endDate).getTime() < today
    })
}

export const isFormatReadOnly = (format: Format | null): boolean => {
    if (!format) return true
    if (!format.state) return false
    return NOT_EDITABLE_STATUSES_FORMAT.includes(format.state)
}

export const isFormatTypeSeries = (format: Format | null | undefined): boolean => {
    if (!format) return false
    return format.type === 'SERIES'
}

// Why: keep state transitions aligned with the review workflow and prevent direct publishing from drafts.
export const getNextFormatState = (fromState: FormatState | undefined): FormatState | undefined => {
    switch (fromState) {
        case 'DRAFT':
        case 'REVISION':
            return 'REVIEW'
        case 'APPROVAL':
            return 'PUBLISHED'
        default:
            return fromState
    }
}

export const shouldPromptPublishUpdate = (
    format: Pick<Format, 'state' | 'hasDraftVersion'> | null | undefined
): boolean => format?.state === 'PUBLISHED' && format.hasDraftVersion === true

export const getFormatEditRouteName = (
    format: Pick<Format, 'state'>
): 'organizer-format-summary' | 'format-format-form' =>
    format.state === 'APPROVAL' || format.state === 'PUBLISHED'
        ? 'organizer-format-summary'
        : 'format-format-form'

// WHY: StepParticipationForm should only appear when at least one event offers both
// on-site and digital attendance (HYBRID). Only then does it make sense to ask the
// participant which form of participation they prefer.
export const hasHybridEvent = (events: Event[] | undefined): boolean =>
    events?.some((event) => event.modality === 'HYBRID') ?? false

export const getJobPostingTitle = (
    item: Record<string, unknown>,
    t: ReturnType<typeof useTranslation>['t']
): string => {
    const profileType =
        typeof item.searchProfileType === 'string' ? item.searchProfileType : undefined
    if (profileType === 'GENERAL') return t('ui.jobPostings.list.generalRole')

    const title = typeof item.title === 'string' ? item.title.trim() : ''
    return title.length > 0 ? title : t('ui.jobPostings.list.untitled')
}
